"use client";

import { combineReducers, configureStore } from "@reduxjs/toolkit";
import { persistReducer, persistStore } from "redux-persist";
import storage from "redux-persist/lib/storage";

import { messageHandlerReducer } from "@/libs/shared/messages/messages.slice";
import { authApi } from "@/libs/auth/redux/auth.api";
import { customersApi } from "@/libs/customers/redux/customers.api";
import { groupsApi } from "@/libs/groups/redux/groups.api";
import { vodsApi } from "@/libs/vods/redux/vod.api";
import { calendarApi } from "@/libs/calendar/redux/calendar.api";
import { tagsApi } from "@/libs/tags/redux/tags.api";
import { trainingPlansApi } from "@/libs/training-plans/redux/trainingPlans.api";
import { settingsApi } from "@/libs/settings/redux/settings.api";
import { dashboardApi } from "@/libs/dashboard/redux/dashboard.api";
import { authReducer } from "@/libs/auth/redux/auth.slice";
import { productsApi } from "@/libs/products/redux/products.api";
import { surveysApi } from "@/libs/surveys/redux/surveys.api";
import { notificationsApi } from "@/libs/notifications/redux/notifications.api";
import { migrationApi } from "@/libs/migration/redux/migration.api";
import { nutritionPlansApi } from "@/libs/nutrition-plans/redux/nutrition-plans.api";
import { checkoutApi } from "@/libs/checkout/redux/checkout.api";

const rootReducer = combineReducers({
  [authApi.reducerPath]: authApi.reducer,
  [customersApi.reducerPath]: customersApi.reducer,
  [notificationsApi.reducerPath]: notificationsApi.reducer,
  [groupsApi.reducerPath]: groupsApi.reducer,
  [vodsApi.reducerPath]: vodsApi.reducer,
  [calendarApi.reducerPath]: calendarApi.reducer,
  [tagsApi.reducerPath]: tagsApi.reducer,
  [trainingPlansApi.reducerPath]: trainingPlansApi.reducer,
  [settingsApi.reducerPath]: settingsApi.reducer,
  [productsApi.reducerPath]: productsApi.reducer,
  [dashboardApi.reducerPath]: dashboardApi.reducer,
  [surveysApi.reducerPath]: surveysApi.reducer,
  [migrationApi.reducerPath]: migrationApi.reducer,
  [nutritionPlansApi.reducerPath]: nutritionPlansApi.reducer,
  [checkoutApi.reducerPath]: checkoutApi.reducer,
  auth: authReducer,
  messageHandler: messageHandlerReducer,
});

const persistConfig = {
  key: "revoo",
  whitelist: ["auth"],
  storage,
};

const conf = (reducers: typeof rootReducer) =>
  configureStore({
    reducer: reducers,
    devTools: true,
    middleware: (getDefaultMiddleware) =>
      getDefaultMiddleware({
        serializableCheck: false,
      }).concat(
        authApi.middleware,
        customersApi.middleware,
        notificationsApi.middleware,
        groupsApi.middleware,
        vodsApi.middleware,
        calendarApi.middleware,
        tagsApi.middleware,
        trainingPlansApi.middleware,
        settingsApi.middleware,
        dashboardApi.middleware,
        productsApi.middleware,
        surveysApi.middleware,
        migrationApi.middleware,
        nutritionPlansApi.middleware,
        checkoutApi.middleware,
      ),
  });

const makeConfiguredStore = () => conf(rootReducer);

export const makeStore = () => {
  if (typeof window === "undefined") {
    return makeConfiguredStore();
  } else {
    const persistedReducer = persistReducer(persistConfig, rootReducer);
    const store: any = conf(persistedReducer as any);
    store.__persistor = persistStore(store); // Nasty hack
    return store;
  }
};

// Get the type of our store variable
export type AppStore = ReturnType<typeof conf>;

// Infer the `RootState` and `AppDispatch` types from the store itself
export type RootState = ReturnType<AppStore["getState"]>;

// Inferred type: {posts: PostsState, comments: CommentsState, users: UsersState}
export type AppDispatch = AppStore["dispatch"];
